import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Home, Sofa, ShieldCheck } from "lucide-react";

export default function HomeCoverageCalculator({ product, quoteHref }) {
  const { format } = useCurrency();
  const meta = product?.meta || {};
  const plans = meta.plans || [];
  const ptypes = meta.property_types || [];
  const baseRate = Number(meta.base_rate_per_100k || 120);
  const contentsRate = Number(meta.contents_rate_per_100k || 150);
  const onlinePct = Number(meta.online_discount_pct || 10);
  const taxPct = Number(meta.tax_pct || 8);
  const bMin = Number(meta.building_min || 50000);
  const bMax = Number(meta.building_max || 2000000);
  const cMin = Number(meta.contents_min || 10000);
  const cMax = Number(meta.contents_max || 500000);

  const [planKey, setPlanKey] = useState("enhanced");
  const [propType, setPropType] = useState("landed");
  const [building, setBuilding] = useState(500000);
  const [contents, setContents] = useState(50000);

  useEffect(() => {
    if (plans.length && !plans.find((p) => p.key === planKey)) {
      setPlanKey(plans[1]?.key || plans[0].key);
    }
    if (ptypes.length && !ptypes.find((t) => t.key === propType)) {
      setPropType(ptypes[0].key);
    }
  }, [product]);

  const plan = useMemo(() => plans.find((p) => p.key === planKey) || plans[0], [plans, planKey]);
  const prop = useMemo(() => ptypes.find((t) => t.key === propType) || ptypes[0], [ptypes, propType]);

  const calc = useMemo(() => {
    if (!plan || !prop) return null;

    const buildingC = (building / 100000) * baseRate * plan.building_mult * prop.multiplier;
    const contentsC = (contents / 100000) * contentsRate * plan.contents_mult;

    const gross = buildingC + contentsC;
    const discount = gross * (onlinePct / 100);
    const subtotal = gross - discount;
    const tax = subtotal * (taxPct / 100);

    return {
      building: round2(buildingC),
      contents: round2(contentsC),
      gross: round2(gross),
      discount: round2(discount),
      subtotal: round2(subtotal),
      tax: round2(tax),
      total: round2(subtotal + tax),
    };
  }, [plan, prop, building, contents]);

  const animatedTotal = useAnimatedNumber(calc?.total ?? 0);

  if (!plans.length || !ptypes.length) return null;

  const deepLink =
    quoteHref +
    `?plan=${planKey}&property_type=${propType}&building=${building}&contents=${contents}`;

  return (
    <section className="max-w-7xl mx-auto px-4 py-16">

      <div className="mb-10">
        <div className="text-xs text-primary uppercase tracking-widest font-semibold mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Coverage calculator
        </div>
        <h2 className="text-4xl font-semibold">Tune your cover. Watch your price.</h2>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">

        {/* LEFT */}
        <div className="lg:col-span-3 relative rounded-2xl border border-primary/20 bg-white p-6 shadow">

          <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute -bottom-20 -right-14 w-60 h-60 rounded-full bg-primary/20 blur-3xl" />

          <div className="space-y-6">

            {/* PLAN */}
            <div>
              <div className="text-xs uppercase text-gray-500 mb-2">Plan</div>
              <div className="flex gap-2 flex-wrap">
                {plans.map((p) => {
                  const on = planKey === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setPlanKey(p.key)}
                      className={`px-4 py-2 rounded-full border ${
                        on
                          ? "bg-primary text-white border-primary"
                          : "bg-white border-gray-200 hover:border-primary hover:text-primary"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* PROPERTY */}
            <div>
              <div className="text-xs uppercase text-gray-500 mb-2">Property type</div>
              <div className="flex gap-2 flex-wrap">
                {ptypes.map((t) => {
                  const on = propType === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setPropType(t.key)}
                      className={`px-4 py-2 rounded-full border ${
                        on
                          ? "bg-primary/10 text-primary border-primary"
                          : "border-gray-200"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* BUILDING */}
            <Slider
              label="Building"
              value={building}
              min={bMin}
              max={bMax}
              onChange={setBuilding}
              format={format}
            />

            {/* CONTENTS */}
            <Slider
              label="Contents"
              value={contents}
              min={cMin}
              max={cMax}
              onChange={setContents}
              format={format}
            />

          </div>
        </div>

        {/* RIGHT CARD */}
        <div className="lg:col-span-2 bg-[#0a1f3d] text-white p-6 rounded-2xl shadow">

          <div className="text-primary uppercase text-xs mb-2">Your estimate</div>

          <div className="text-5xl font-bold">
            {format(animatedTotal)}
          </div>

          <ul className="mt-4 space-y-2 text-sm">
            <Row label="Building" value={format(calc?.building)} />
            <Row label="Contents" value={format(calc?.contents)} />
            <Row label="Discount" value={`-${format(calc?.discount)}`} />
            <Row label="Tax" value={format(calc?.tax)} />
            <Row label="Total" value={format(calc?.total)} big />
          </ul>

          <Link to={deepLink}>
            <Button className="w-full mt-6 bg-primary hover:bg-primary-600 text-white">
              Get this cover <ArrowRight className="ml-2" />
            </Button>
          </Link>

        </div>
      </div>
    </section>
  );
}

/* helpers */

function Slider({ label, value, min, max, onChange, format }) {
  return (
    <div>
      <div className="flex justify-between mb-2">
        <span>{label}</span>
        <span className="text-primary font-semibold">{format(value)}</span>
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
      />
    </div>
  );
}

function Row({ label, value, big }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className={big ? "font-bold text-lg" : ""}>{value}</span>
    </div>
  );
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function useAnimatedNumber(target) {
  const [val, setVal] = useState(target);

  useEffect(() => {
    let start = val;
    let frame;

    const animate = () => {
      start += (target - start) * 0.1;
      if (Math.abs(target - start) < 0.1) {
        setVal(target);
        return;
      }
      setVal(start);
      frame = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return val;
}